/**
 * RecordsManager — Persistent best-games history via localStorage.
 *
 * Stores the top 5 game runs, ranked by score (descending).
 * Each record includes: score, level, coins, agmuths, time played.
 *
 * All functions are named exports. Data is a JSON array in localStorage.
 */

import {
  RECORDS_STORAGE_KEY,
  RECORDS_MAX_ENTRIES,
} from '../config/game-config.js';

/**
 * @typedef {Object} GameRecord
 * @property {number} score     - Final score
 * @property {number} level     - Level reached
 * @property {number} coins     - Total coins collected
 * @property {number} agmuths   - Agmuths collected
 * @property {number} timePlayed - Seconds played
 * @property {number} timestamp  - Date.now() when the record was saved
 */

/**
 * Load records from localStorage.
 * @returns {GameRecord[]} Sorted by score descending, max RECORDS_MAX_ENTRIES
 */
export function getRecords() {
  try {
    const raw = localStorage.getItem(RECORDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn('RecordsManager: corrupted data, discarding.');
      localStorage.removeItem(RECORDS_STORAGE_KEY);
      return [];
    }
    // Validate and sanitize each entry
    const valid = parsed
      .filter((r) =>
        r && typeof r === 'object'
        && typeof r.score === 'number'
        && typeof r.level === 'number',
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, RECORDS_MAX_ENTRIES);
    return valid;
  } catch (err) {
    console.warn('RecordsManager: failed to load:', err);
    return [];
  }
}

/**
 * Save records to localStorage.
 * @private
 * @param {GameRecord[]} records
 */
function _save(records) {
  try {
    localStorage.setItem(RECORDS_STORAGE_KEY, JSON.stringify(records));
  } catch (err) {
    console.warn('RecordsManager: failed to save:', err);
  }
}

/**
 * Add a new record. Inserts in sorted order and trims to max entries.
 * Returns the rank (1-based) if it made it into the top list, or 0 if not.
 * @param {GameRecord} record
 * @returns {number} Rank (1-5) or 0 if not a top score
 */
export function addRecord(record) {
  const records = getRecords();

  // Insert into sorted position
  let insertIdx = records.findIndex((r) => record.score > r.score);
  if (insertIdx === -1) insertIdx = records.length;

  // Check if it would make the cut
  if (insertIdx >= RECORDS_MAX_ENTRIES) return 0;

  records.splice(insertIdx, 0, {
    score: record.score ?? 0,
    level: record.level ?? 1,
    coins: record.coins ?? 0,
    agmuths: record.agmuths ?? 0,
    timePlayed: record.timePlayed ?? 0,
    timestamp: record.timestamp ?? Date.now(),
  });

  // Trim to max entries
  if (records.length > RECORDS_MAX_ENTRIES) {
    records.length = RECORDS_MAX_ENTRIES;
  }

  _save(records);
  return insertIdx + 1; // 1-based rank
}

/**
 * Get the count of stored records.
 * @returns {number}
 */
export function getRecordCount() {
  return getRecords().length;
}

/**
 * Check if a score would make it into the top list.
 * @param {number} score
 * @returns {boolean}
 */
export function isNewRecord(score) {
  const records = getRecords();
  if (records.length < RECORDS_MAX_ENTRIES) return true;
  return score > records[records.length - 1].score;
}

/**
 * Clear all records.
 */
export function clearRecords() {
  try {
    localStorage.removeItem(RECORDS_STORAGE_KEY);
  } catch (err) {
    console.warn('RecordsManager: failed to clear:', err);
  }
}
